
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/MButton.vue";
import MIcon from "@/components/MIcon.vue";
import MSelectBox from "@/components/form/MSelectBox.vue";
import MTextField from "@/components/form/MTextField.vue";
import MMultiSelectBox from "@/components/form/MMultiSelectBox.vue";
import store from "@/store";
import { Options, Vue } from "vue-class-component";
import { Classroom } from "@/entities/classroom";
import { TutorAuthority } from "@/entities/tutor";
import { registerTutor, TutorMeta } from "@/api/tutor";
import { saveErrorLog } from "@/api/error";
import { exportToUTF8Csv, readAsText } from "@/utils/csv";
import { validEmail } from "@/utils/email";
import {
  validateTutorName,
  validateAuthorityDisplayName,
  validateClassroomNames,
  validateTutorPassword
} from "@/utils/tutor";
import parse from "csv-parse/lib/sync";
import { parseClassroomNames } from "@/utils/classroom";
import {
  detectStaffAddCsvLayout,
  PASSWORD_LOGIN_DISABLED_HEADER,
  PASSWORD_LOGIN_LOCKOUT_MESSAGE,
  readPasswordLoginCell
} from "@/utils/passwordLoginCsv";

@Options({
  components: {
    MBaseModal,
    MButton,
    MSelectBox,
    MTextField,
    MMultiSelectBox,
    MIcon
  },
  emits: ["close"],
  props: {
    tutor: Object,
    classrooms: Array
  }
})
export default class MsAddModal extends Vue {
  DEFAULT_PASSWORD = "12345678";
  name = "";
  enteredName = false;
  email = "";
  enteredEmail = false;
  password = "";
  authorityText = "一般";
  authorityTexts: string[] = [];
  selectedClassroomIds: string[] = [];
  tutor!: TutorMeta;
  classrooms: Classroom[] = [];
  googleAccountEmail = "";
  microSoftAccountEmail = "";
  passwordLoginDisabled = false;
  validateEmail = validEmail;

  get isSchool() {
    return store.state.isSchool;
  }

  get authorityMap(): Partial<Record<TutorAuthority, string>> {
    return this.isSchool
      ? {
          admin: "ICT担当者",
          employee: "先生"
        }
      : {
          admin: "管理者",
          supervisor: "責任者",
          employee: "担当者",
          general: "一般"
        };
  }

  get classroomMap(): Record<string, string> {
    return store.state.classrooms
      .filter(r => r.ref.parent?.parent?.id === store.state.school?.ref.id)
      .reduce((acc: Record<string, string>, cur) => {
        acc[cur.ref.id] = cur.data.name;
        return acc;
      }, {});
  }

  get selectClassrooms(): Classroom[] {
    return this.classrooms.filter(item =>
      this.selectedClassroomIds.includes(item.ref.id)
    );
  }

  get authority(): TutorAuthority {
    if (["管理者", "ICT担当者"].includes(this.authorityText)) {
      return "admin";
    } else if (this.authorityText === "責任者") {
      return "supervisor";
    } else if (["担当者", "先生"].includes(this.authorityText)) {
      return "employee";
    } else {
      return "general";
    }
  }

  get classroomsPlaceholder(): string {
    let text = `${this.selectClassrooms.length}教室`;
    if (this.selectClassrooms.length === 0) {
      return text;
    }
    text += ": ";
    text += this.selectClassrooms.map(item => item.data.name).join(", ");
    return text;
  }

  get validGoogleAccountEmail(): boolean {
    if (!this.googleAccountEmail) return true;
    return this.validateEmail(this.googleAccountEmail);
  }

  get validMicroSoftAccountEmail(): boolean {
    if (!this.microSoftAccountEmail) return true;
    return this.validateEmail(this.microSoftAccountEmail);
  }

  validateAuthorityName(val: string): boolean {
    return validateAuthorityDisplayName(
      val,
      Object.values(this.authorityMap) as string[]
    );
  }

  validateClassroomNamesLocal(classroomNames: string[]): boolean {
    return validateClassroomNames(
      classroomNames,
      Object.values(this.classroomMap)
    );
  }

  get validName() {
    return validateTutorName(this.name);
  }

  get validEmail(): boolean {
    return this.validateEmail(this.email);
  }

  get validPassword(): boolean {
    return validateTutorPassword(this.password);
  }

  get validData() {
    return (
      this.validName &&
      this.email &&
      this.validPassword &&
      this.validGoogleAccountEmail &&
      this.validMicroSoftAccountEmail
    );
  }

  get allEntered() {
    return this.enteredName && this.enteredEmail;
  }

  changeClassroom(id: string) {
    if (this.selectedClassroomIds.includes(id)) {
      this.selectedClassroomIds = this.selectedClassroomIds.filter(
        item => item !== id
      );
    } else {
      this.selectedClassroomIds.push(id);
    }
  }

  allEnter() {
    this.enteredName = true;
    this.enteredEmail = true;
  }

  translateErrorMessage(message: string): string {
    if (
      message.includes("This google account is already in use") ||
      message.includes("This Gmail is already in use") ||
      message.includes("This gmail is already in use") ||
      message.includes("`googleAccountEmail` is already in use")
    ) {
      return "すでに使われているGoogleアカウントです";
    } else if (
      message.includes("This microsoft account is already in use") ||
      message.includes("This microsoft email is already in use") ||
      message.includes("This MicrosoftEmail is already in use") ||
      message.includes("`microSoftAccountEmail` is already in use")
    ) {
      return "すでに使われているMicrosoftアカウントです";
    } else {
      return message;
    }
  }

  async register() {
    this.allEnter();

    if (!this.validData) {
      return;
    }

    // 先生アカウント数の上限チェック（0を含む数値の場合はチェックする）
    const school = store.state.school;
    const schoolConfig = store.state.schoolConfig;
    const maxTutorCount = schoolConfig?.data.maxTutorCount;
    if (school && typeof maxTutorCount === "number") {
      const currentTutorCount = school.data.tutors.length;
      if (currentTutorCount >= maxTutorCount) {
        alert(
          `先生アカウントの最大数（${maxTutorCount}件）を超えるため、登録できません。\n` +
            `現在: ${currentTutorCount}件`
        );
        return;
      }
    }

    store.commit("START_LOADING", "スタッフ登録中...");

    try {
      await registerTutor(
        this.name.trim(),
        this.email,
        this.password,
        this.authority,
        this.authority === "general" ||
          this.authority === "employee" ||
          this.authority === "supervisor"
          ? this.selectedClassroomIds
          : [],
        this.googleAccountEmail,
        this.microSoftAccountEmail,
        this.passwordLoginDisabled
      );
      store.commit("END_LOADING");
    } catch (e) {
      store.commit("END_LOADING");
      alert(
        e instanceof Error
          ? this.translateErrorMessage(e.message ?? "Unknown error")
          : "Unknown error"
      );
      await saveErrorLog(
        store.state.role,
        e.code,
        e.message,
        "Failed to register tutor"
      );
      return;
    }

    this.$router.go(0);
  }

  downloadCsvTemplate() {
    const headers: { key: string; displayKey: string }[] = [
      { key: "name", displayKey: "スタッフ名" },
      { key: "email", displayKey: "メールアドレス" },
      { key: "authority", displayKey: "権限" },
      {
        key: "googleAccountEmail",
        displayKey: "Googleアカウント"
      },
      {
        key: "microsoftAccountEmail",
        displayKey: "Microsoftアカウント"
      },
      {
        key: "passwordLoginDisabled",
        displayKey: "パスワードログインを無効にする"
      },
      {
        key: "classroomNames",
        displayKey: "確認権限を持つ教室名一覧（コンマ区切り）"
      }
    ];
    exportToUTF8Csv(
      headers.map(_ => _.displayKey).join(","),
      "スタッフ登録用紙"
    );
  }

  validateStaffData(staffData: {
    name: string;
    email: string;
    authority: string;
    googleAccountEmail?: string;
    microsoftAccountEmail?: string;
    classroomNames: string[];
  }) {
    const {
      name,
      email,
      authority,
      classroomNames,
      googleAccountEmail,
      microsoftAccountEmail
    } = staffData;

    let validEmail = false;
    let validName = false;
    let validAuthority = false;
    let validClassroomNames = false;
    let validGoogleAccountEmail = false;
    let validMicrosoftAccountEmail = false;

    if (validateTutorName(name)) {
      validName = true;
    }

    if (this.validateEmail(email)) {
      validEmail = true;
    }
    if (this.validateAuthorityName(authority)) {
      validAuthority = true;
    }
    if (this.validateClassroomNamesLocal(classroomNames)) {
      validClassroomNames = true;
    }

    if (googleAccountEmail) {
      validGoogleAccountEmail = this.validateEmail(googleAccountEmail);
    } else {
      validGoogleAccountEmail = true;
    }

    if (microsoftAccountEmail) {
      validMicrosoftAccountEmail = this.validateEmail(microsoftAccountEmail);
    } else {
      validMicrosoftAccountEmail = true;
    }

    return {
      validName,
      validEmail,
      validAuthority,
      validClassroomNames,
      validGoogleAccountEmail,
      validMicrosoftAccountEmail
    };
  }

  async handleCsvRegistration(event: Event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.files || event.target.files.length === 0) return;
    let text: string | ArrayBuffer | null = null;

    try {
      text = await readAsText(event.target.files[0], { onlyUtf8: true });
    } catch (e) {
      console.error(e);
      alert(
        (e instanceof Error && e.message === "File must be UTF-8 encoded"
          ? "エンコードに使用されている文字コードがおかしいようです。"
          : "CSVを解析できませんでした。") +
          "\nCSVフォーマットを新たにダウンロードして内容を貼り直して再度登録してみてください。"
      );
      event.target.value = "";
      return;
    }

    event.target.value = ""; //input要素のfile入力をリセット

    if (typeof text !== "string") {
      throw new Error(
        "ファイルを正常に読み込むことができませんでした。CSVファイルのデータが正しいかどうか確認してください"
      );
    }

    const rows: string[][] = parse(text, {
      skipEmptyLines: true,
      relaxColumnCount: true
    }).map((row: string[]) => row.map(col => String(col ?? "").trim()));

    const header = rows[0] ?? [];
    let layout: ReturnType<typeof detectStaffAddCsvLayout>;
    try {
      layout = detectStaffAddCsvLayout(header);
    } catch (e) {
      alert(
        e instanceof Error ? e.message : "CSVフォーマットが正しくありません。"
      );
      return;
    }

    const filtered = rows
      .filter((_, i) => i > 0)
      .filter(row => row.some(col => String(col).trim()));
    if (!filtered.length) return;

    const parsedStaffDatas = filtered.reduce(
      (
        acc: {
          name: string;
          email: string;
          authority: string;
          googleAccountEmail: string;
          microSoftAccountEmail: string;
          classroomNames: string[];
          passwordLoginRaw: string | null;
        }[],
        cur: string[]
      ) => {
        const classroomNamesRaw = cur
          .filter((_, i) => i >= layout.classroomStartIndex)
          .map(col => String(col ?? "").trim())
          .filter(Boolean)
          .join(",");
        const classroomNames: string[] = parseClassroomNames(classroomNamesRaw);
        const staff = {
          name: cur[0],
          email: cur[1],
          authority: cur[2],
          googleAccountEmail: cur[3] ?? "",
          microSoftAccountEmail: cur[4] ?? "",
          classroomNames,
          passwordLoginRaw:
            layout.kind === "withFlag"
              ? cur[layout.flagIndex] ?? ""
              : null
        };
        return [...acc, staff];
      },
      []
    );

    const usedEmails: string[] = [];
    const usedGmails: string[] = [];
    const usedMicrosoftEmails: string[] = [];
    for (const [index, data] of parsedStaffDatas.entries()) {
      //個々のチェック
      const validationResult = this.validateStaffData(data);
      const invalidKeys: string[] = [];
      Object.entries(validationResult).forEach(([key, val]) => {
        if (!val) invalidKeys.push(key);
      });

      if (invalidKeys.length > 0) {
        const text = invalidKeys
          .join(",")
          .replace("validName", "名前")
          .replace("validEmail", "メールアドレス")
          .replace("validAuthority", "権限")
          .replace("validClassroomNames", "教室名")
          .replace("validGoogleAccountEmail", "Googleアカウント")
          .replace("validMicrosoftAccountEmail", "Microsoftアカウント");
        alert(`${index + 1}番目のデータで${text}に誤りがあります。`);
        return;
      }

      const passwordLogin = readPasswordLoginCell(
        data.passwordLoginRaw ?? undefined,
        "allow"
      );
      if (!passwordLogin.ok) {
        alert(
          `${
            index + 1
          }番目のデータで${PASSWORD_LOGIN_DISABLED_HEADER}に誤りがあります。`
        );
        return;
      }
      const passwordLoginDisabled =
        passwordLogin.intent.kind === "set"
          ? passwordLogin.intent.disabled
          : false;
      if (
        passwordLoginDisabled &&
        !data.googleAccountEmail &&
        !data.microSoftAccountEmail
      ) {
        alert(
          `${
            index + 1
          }番目のデータで${PASSWORD_LOGIN_DISABLED_HEADER}に誤りがあります。\n${PASSWORD_LOGIN_LOCKOUT_MESSAGE}`
        );
        return;
      }

      //重複チェック
      const { email, googleAccountEmail, microSoftAccountEmail } = data;
      if (usedEmails.includes(email)) {
        alert(
          `登録しようとしているデータ内で、メールアドレスに重複があります。` +
            "\n" +
            `重複しているメールアドレス: ${email}`
        );
        return;
      } else {
        usedEmails.push(email);
      }

      if (googleAccountEmail) {
        if (usedGmails.includes(googleAccountEmail)) {
          alert(
            `登録しようとしているデータ内で、メールアドレスに重複があります。` +
              "\n" +
              `重複しているメールアドレス: ${googleAccountEmail}`
          );
          return;
        } else {
          usedGmails.push(googleAccountEmail);
        }
      }

      if (microSoftAccountEmail) {
        if (usedMicrosoftEmails.includes(microSoftAccountEmail)) {
          alert(
            `登録しようとしているデータ内で、メールアドレスに重複があります。` +
              "\n" +
              `重複しているメールアドレス: ${microSoftAccountEmail}`
          );
          return;
        } else {
          usedMicrosoftEmails.push(microSoftAccountEmail);
        }
      }
    }

    try {
      store.commit("START_LOADING", "スタッフ登録中...");

      await this.registerStaffViaCsv(parsedStaffDatas);
      this.$router.go(0);
    } catch (e) {
      alert("一部スタッフの登録に失敗しました。");
      console.error(e);
    } finally {
      store.commit("END_LOADING");
    }
  }

  async registerStaffViaCsv(
    datas: {
      name: string;
      email: string;
      authority: string;
      googleAccountEmail: string;
      microSoftAccountEmail: string;
      classroomNames: string[];
      passwordLoginRaw: string | null;
    }[]
  ) {
    // 先生アカウント数の上限チェック（0を含む数値の場合はチェックする）
    const school = store.state.school;
    const schoolConfig = store.state.schoolConfig;
    const maxTutorCount = schoolConfig?.data.maxTutorCount;
    if (school && typeof maxTutorCount === "number") {
      const currentTutorCount = school.data.tutors.length;
      const newTutorCount = datas.length;
      if (currentTutorCount + newTutorCount > maxTutorCount) {
        alert(
          `先生アカウントの最大数（${maxTutorCount}件）を超えるため、登録できません。\n` +
            `現在: ${currentTutorCount}件、登録予定: ${newTutorCount}件`
        );
        return;
      }
    }

    const failures: { name: string; error: string }[] = [];
    let successes = 0;

    for (const data of datas) {
      const {
        name,
        email,
        authority: _authority,
        classroomNames,
        googleAccountEmail,
        microSoftAccountEmail,
        passwordLoginRaw
      } = data;
      const passwordLogin = readPasswordLoginCell(
        passwordLoginRaw ?? undefined,
        "allow"
      );
      const passwordLoginDisabled =
        passwordLogin.ok && passwordLogin.intent.kind === "set"
          ? passwordLogin.intent.disabled
          : false;
      const authority = Object.entries(this.authorityMap).find(
        ([, val]) => val === _authority
      )?.[0] as TutorAuthority;
      const classroomIds: string[] =
        classroomNames.length > 0
          ? classroomNames.map(name => {
              return Object.entries(this.classroomMap).find(
                ([, val]) => val === name
              )?.[0] as string;
            })
          : [];

      try {
        await registerTutor(
          name.trim(),
          email,
          this.DEFAULT_PASSWORD,
          authority,
          authority === "general" ||
            authority === "employee" ||
            authority === "supervisor"
            ? classroomIds
            : [],
          googleAccountEmail,
          microSoftAccountEmail,
          passwordLoginDisabled
        );
        successes++;
      } catch (e) {
        failures.push({
          name: data.name,
          error:
            e instanceof Error
              ? this.translateErrorMessage(e.message ?? "Unknown error")
              : "Unknown error"
        });
        console.error(e);
      }
    }

    const text = `
成功件数: ${successes}件
失敗件数: ${failures.length}件

【失敗詳細】
${failures.map(failure => `${failure.name} : ${failure.error}`).join("\n")}
`.trim();
    alert(text);
  }

  close() {
    this.$emit("close");
  }

  created() {
    this.password = this.DEFAULT_PASSWORD;
    this.authorityText = this.isSchool ? "先生" : "一般";

    if (!this.isSchool) {
      if (this.tutor.config.data.authority === "owner") {
        this.authorityTexts = ["一般", "担当者", "責任者", "管理者"];
      } else if (this.tutor.config.data.authority === "admin") {
        this.authorityTexts = ["一般", "担当者", "責任者"];
      } else if (this.tutor.config.data.authority === "supervisor") {
        this.authorityTexts = ["一般", "担当者"];
      } else {
        this.authorityTexts = ["一般"];
      }
    } else {
      if (this.tutor.config.data.authority === "owner") {
        this.authorityTexts = ["先生", "ICT担当者"];
      } else if (this.tutor.config.data.authority === "admin") {
        this.authorityTexts = ["先生"];
      } else if (this.tutor.config.data.authority === "supervisor") {
        this.authorityTexts = ["先生"];
      }
    }
  }
}
